"use client";

import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState, type ReactNode, type SVGProps } from "react";

import { persistAppLocaleCookie } from "@/config/app-locale";
import {
  marketingCopy,
  type MarketingLocale,
} from "@/features/marketing/content/marketing-copy";

import styles from "./marketing-home.module.css";

const LOGIN_PATH = "/12connex/login";
const ORGANIZATIONS_URL = "https://12.cvbuilder.ca/";
const CONTACT_EMAIL = "team@cvbuilder.ca";
const FACEBOOK_URL = "https://www.facebook.com/12connex/";
const INSTAGRAM_URL = "https://www.instagram.com/12connex/";

type MarketingHomeProps = {
  locale: MarketingLocale;
};

// The marketing site and the tenant app track locale via separate mechanisms
// (a `lang` query param here vs. the cv_app_locale cookie there), so CTAs that
// cross from one into the other must carry the locale forward explicitly.
function carryLocaleIntoApp(locale: MarketingLocale): void {
  persistAppLocaleCookie(locale);
}

function pricingPath(locale: MarketingLocale) {
  return locale === "fr" ? "/pricing?lang=fr" : "/pricing";
}

type MarketingChromeProps = MarketingHomeProps & {
  linkToHomeSections?: boolean;
  localePath?: string;
};

export function MarketingHome({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale];

  return (
    <div className={styles.page}>
      <MarketingHeader locale={locale} />

      <main>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <div className={styles.heroCopy}>
              <p className={styles.badge}><span aria-hidden="true" />{copy.badge}</p>
              <h1>{copy.title}</h1>
              <p className={styles.description}>{copy.description}</p>
              <div className={styles.ctas}>
                <a href={LOGIN_PATH} className={styles.primaryCta} onClick={() => carryLocaleIntoApp(locale)}>
                  {copy.nav.createResume}<ArrowUpRight aria-hidden="true" />
                </a>
                <a href="#how-it-works" className={styles.secondaryCta}>
                  <span>{copy.howItWorksCta}</span><span aria-hidden="true">↓</span>
                </a>
              </div>
              <ul className={styles.benefits}>
                {copy.benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}
              </ul>
            </div>

            <BuilderMockup locale={locale} />
          </div>
        </section>
        <PricingTeaser locale={locale} />
        <HowItWorksSection locale={locale} />
        <FeatureSection locale={locale} />
        <MiniCtaSection locale={locale} />
        <BenefitsSection locale={locale} />
        <OrganizationBanner locale={locale} />
      </main>
      <MarketingFooter locale={locale} />
    </div>
  );
}

function PricingTeaser({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].pricingTeaser;

  return (
    <section className={styles.pricingTeaser} aria-label={copy.fullLabel}>
      <div className={styles.pricingTeaserInner}>
        <div className={styles.pricingOption}>
          <strong>{copy.freeLabel}</strong>
          <p>{copy.freeDescription}</p>
        </div>
        <div className={styles.pricingOption}>
          <strong>{copy.fullLabel}</strong>
          <p>{copy.fullDescription}</p>
        </div>
        <Link href={pricingPath(locale)} className={styles.pricingTeaserLink}>
          {copy.action}<ArrowUpRight aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

function sectionHref(hash: string, locale: MarketingLocale, linkToHomeSections: boolean) {
  return linkToHomeSections ? `/?lang=${locale}${hash}` : hash;
}

function localizedPolicyHref(path: string, locale: MarketingLocale) {
  return locale === "fr" ? `${path}?lang=fr` : path;
}

export function MarketingHeader({
  locale,
  linkToHomeSections = false,
  localePath = "/",
}: MarketingChromeProps) {
  const copy = marketingCopy[locale];

  return (
    <header className={styles.header}>
      <nav className={styles.nav} aria-label="Primary navigation">
        <Link href={`/?lang=${locale}`} className={styles.brand} aria-label="CVBuilder.ca home">
          <Image
            src="/logo/Logo CV Builder.jpg"
            alt=""
            width={48}
            height={48}
            className={styles.logo}
            priority
          />
          <span><strong>CV</strong>Builder<span>.ca</span></span>
        </Link>

        <div className={styles.navLinks}>
          <a href={sectionHref("#how-it-works", locale, linkToHomeSections)}>{copy.nav.howItWorks}</a>
          <a href={sectionHref("#features", locale, linkToHomeSections)}>{copy.nav.features}</a>
          <a href={sectionHref("#benefits", locale, linkToHomeSections)}>{copy.nav.benefits}</a>
          <Link href={pricingPath(locale)}>{copy.nav.pricing}</Link>
          <a href={ORGANIZATIONS_URL} target="_blank" rel="noopener noreferrer">{copy.nav.organizations}</a>
        </div>

        <div className={styles.navActions}>
          <div className={styles.localeSwitch} aria-label="Language">
            <Link href={`${localePath}?lang=en`} aria-current={locale === "en" ? "page" : undefined}>EN</Link>
            <span>/</span>
            <Link href={`${localePath}?lang=fr`} aria-current={locale === "fr" ? "page" : undefined}>FR</Link>
          </div>
          <a href={LOGIN_PATH} className={styles.headerCta} onClick={() => carryLocaleIntoApp(locale)}>
            {copy.nav.createResume}
          </a>
        </div>
      </nav>
    </header>
  );
}

export function MarketingFooter({
  locale,
  linkToHomeSections = false,
}: MarketingChromeProps) {
  const copy = marketingCopy[locale].footer;
  const footerHrefs = [
    [LOGIN_PATH, LOGIN_PATH, pricingPath(locale), sectionHref("#how-it-works", locale, linkToHomeSections)],
    [
      localizedPolicyHref("/privacy", locale),
      localizedPolicyHref("/terms", locale),
      localizedPolicyHref("/cookies", locale),
      localizedPolicyHref("/refund", locale),
    ],
  ] as const;

  return (
    <footer className={styles.footer}>
      <div className={styles.footerInner}>
        <div className={styles.footerBrand}>
          <Link href={`/?lang=${locale}`}><strong>CV</strong>Builder.ca</Link>
          <p>{copy.description}</p>
          <strong className={styles.hosting}>
            <CanadaFlagIcon aria-hidden="true" />{copy.hosting}
          </strong>
        </div>

        {copy.groups.map((group, groupIndex) => (
          <nav className={styles.footerGroup} aria-label={group.title} key={group.title}>
            <h2>{group.title}</h2>
            <ul>
              {group.links.map((label, linkIndex) => (
                <li key={label}>
                  <Link
                    aria-label={
                      groupIndex === 1
                        ? `${label} (${locale === "fr" ? "s’ouvre dans un nouvel onglet" : "opens in a new tab"})`
                        : undefined
                    }
                    href={footerHrefs[groupIndex][linkIndex]}
                    onClick={
                      footerHrefs[groupIndex][linkIndex] === LOGIN_PATH
                        ? () => carryLocaleIntoApp(locale)
                        : undefined
                    }
                    rel={groupIndex === 1 ? "noopener noreferrer" : undefined}
                    target={groupIndex === 1 ? "_blank" : undefined}
                  >
                    <span aria-hidden={groupIndex === 1 ? "true" : undefined}>{label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}

        <div className={styles.footerContact}>
          <h2>{copy.contact.title}</h2>
          <div className={styles.footerContactIcons}>
            <a
              className={styles.footerContactIcon}
              href={`mailto:${CONTACT_EMAIL}`}
              aria-label={CONTACT_EMAIL}
            >
              <MailIcon aria-hidden="true" />
            </a>
            <a
              className={styles.footerContactIcon}
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <FacebookIcon aria-hidden="true" />
            </a>
            <a
              className={styles.footerContactIcon}
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <InstagramIcon aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
      <p className={styles.copyright}>{copy.copyright}</p>
    </footer>
  );
}

function CanadaFlagIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 48 24" width="22" height="14" {...props}>
      <rect width="48" height="24" fill="#fff" />
      <rect width="12" height="24" fill="#D52B1E" />
      <rect x="36" width="12" height="24" fill="#D52B1E" />
      <path
        fill="#D52B1E"
        d="M24 4.2l-1.4 2.6c-.2.3-.5.3-.8.1l-1-.5.7 3.6c.1.6-.3.6-.6.2l-1.4-1.6-.2.8c0 .2-.2.3-.4.3l-1.8-.4.5 1.7c.1.4.2.5-.1.7l-.6.3 3.3 2.7c.2.2.3.4.2.7l-.3 1 3.1-.4c.2 0 .4.1.4.3l-.2 3.3h.8l-.2-3.3c0-.2.2-.3.4-.3l3.1.4-.3-1c-.1-.3 0-.5.2-.7l3.3-2.7-.6-.3c-.3-.2-.2-.3-.1-.7l.5-1.7-1.8.4c-.2 0-.4-.1-.4-.3l-.2-.8-1.4 1.6c-.3.4-.7.4-.6-.2l.7-3.6-1 .5c-.3.2-.6.2-.8-.1z"
      />
    </svg>
  );
}

function MailIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m2 7 10 6 10-6" />
    </svg>
  );
}

function FacebookIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
      <path d="M13.5 21v-7.5h2.52l.38-2.93h-2.9V8.7c0-.85.24-1.43 1.45-1.43h1.55V4.64A20.8 20.8 0 0 0 14.1 4.5c-2.3 0-3.87 1.4-3.87 3.98v2.09H7.69v2.93h2.54V21Z" />
    </svg>
  );
}

function InstagramIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function BenefitsSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].benefitsSection;

  return (
    <section className={styles.aboutSection} id="benefits">
      <div className={styles.aboutInner}>
        <div className={styles.aboutGrid}>
          <div className={styles.aboutCopy}>
            <p>{copy.eyebrow}</p>
            <h2>{copy.title}</h2>
            <div>{copy.description}</div>
          </div>

          <div className={styles.aboutPillars}>
            {copy.pillars.map((pillar) => (
              <article key={pillar.title}>
                <h3>{pillar.title}</h3>
                <p>{pillar.description}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].howItWorks;

  return (
    <section className={styles.jobSeekerSection} id="how-it-works">
      <div className={styles.jobSeekerInner}>
        <div className={styles.jobSeekerHeading}>
          <div>
            <p>{copy.eyebrow}</p>
            <h2>{copy.title}</h2>
          </div>
          <a href={LOGIN_PATH} onClick={() => carryLocaleIntoApp(locale)}>{copy.action}<ArrowUpRight aria-hidden="true" /></a>
        </div>

        <ol className={styles.jobSeekerSteps}>
          {copy.steps.map((step, index) => (
            <li key={step.title}>
              <span>{locale === "fr" ? "Étape" : "Step"} {index + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </li>
          ))}
        </ol>

        <p className={styles.jobSeekerNote}>
          <strong>{copy.note.lead}</strong>{copy.note.rest}
        </p>
      </div>
    </section>
  );
}

function MiniCtaSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].miniCta;

  return (
    <section className={styles.miniCtaSection}>
      <div className={styles.miniCtaBox}>
        <h2>{copy.title}</h2>
        <a href={LOGIN_PATH} className={styles.miniCtaAction} onClick={() => carryLocaleIntoApp(locale)}>
          {copy.action}<ArrowUpRight aria-hidden="true" />
        </a>
      </div>
    </section>
  );
}

function OrganizationBanner({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].orgBanner;

  return (
    <section className={styles.orgBanner}>
      <div className={styles.orgBannerInner}>
        <p className={styles.orgBannerText}>
          <strong>{copy.lead}</strong>{copy.rest}
        </p>
        <a
          className={styles.orgBannerAction}
          href={ORGANIZATIONS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {copy.action}<ArrowUpRight aria-hidden="true" />
        </a>
      </div>
    </section>
  );
}

const featureIcons = ["🔎", "🍁", "✨", "🗣️", "🔒", "⏱️"] as const;

function FeatureSection({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].features;

  return (
    <section className={styles.featuresSection} id="features">
      <div className={styles.featuresInner}>
        <p className={styles.featuresEyebrow}>{copy.eyebrow}</p>
        <h2>{copy.title}</h2>
        <div className={styles.featureGrid}>
          {copy.items.map((feature, index) => {
            const icon = featureIcons[index];

            return (
              <article className={styles.featureCard} key={feature.title}>
                <div className={styles.featureMeta}>
                  <span className={styles.featureIcon} aria-hidden="true">{icon}</span>
                  <span className={styles.featureNumber}>{String(index + 1).padStart(2, "0")}</span>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BuilderMockup({ locale }: MarketingHomeProps) {
  const copy = marketingCopy[locale].mockup;
  type PreviewSection = "profile" | "achievements" | "experience" | "education" | "additional";
  const [activeSection, setActiveSection] = useState<PreviewSection>("profile");
  const navigation = [
    { id: "profile", label: copy.profile, icon: "👤" },
    { id: "achievements", label: copy.achievements, icon: "✨" },
    { id: "experience", label: copy.experience, icon: "💼" },
    { id: "education", label: copy.education, icon: "🎓" },
    { id: "additional", label: copy.additional, icon: "🗣️" },
  ] satisfies ReadonlyArray<{ id: PreviewSection; label: string; icon: string }>;

  const previewSections: Record<PreviewSection, ReactNode> = {
    profile: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.profile}</h2>
        <p>{copy.profileText}</p>
      </section>
    ),
    achievements: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.achievements}</h2>
        <ul>{copy.achievementItems.map((item) => <li key={item}>{item}</li>)}</ul>
      </section>
    ),
    experience: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.experience}</h2>
        {copy.experienceItems.map((item) => (
          <article className={styles.resumePreviewEntry} key={item.company}>
            <h3>{item.company} — {item.role}</h3>
            <p className={styles.resumePreviewMeta}>{item.location} | {item.dates}</p>
            <ul>{item.details.map((detail) => <li key={detail}>{detail}</li>)}</ul>
          </article>
        ))}
      </section>
    ),
    education: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.education}</h2>
        {copy.educationItems.map((item) => (
          <article className={styles.resumePreviewEntry} key={item.credential}>
            <h3>{item.credential}</h3>
            <p>{item.institution} | {item.dates}</p>
          </article>
        ))}
      </section>
    ),
    additional: (
      <section className={styles.resumePreviewSection}>
        <h2>{copy.additional}</h2>
        <ul>{copy.additionalItems.map((item) => <li key={item}>{item}</li>)}</ul>
      </section>
    ),
  };

  return (
    <div className={styles.mockup} aria-label="CVBuilder application preview">
      <div className={styles.browserBar}>
        <span /><span /><span />
        <code>cvbuilder.ca / builder</code>
      </div>
      <div className={styles.builder}>
        <aside className={styles.sidebar}>
          <p>{copy.sections}</p>
          <ul role="tablist" aria-label={copy.sections}>
            {navigation.map(({ id, label, icon }) => (
              <li key={id}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSection === id}
                  aria-controls="marketing-resume-preview"
                  className={activeSection === id ? styles.activeNavItem : undefined}
                  onClick={() => setActiveSection(id)}
                >
                  <span className={styles.previewIcon} aria-hidden="true">{icon}</span>
                  <span className={styles.sidebarLabel}>{label}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className={styles.completion}><span />{copy.complete}</div>
        </aside>
        <div
          id="marketing-resume-preview"
          className={styles.workspace}
          role="tabpanel"
          aria-live="polite"
        >
          {previewSections[activeSection]}
        </div>
      </div>
    </div>
  );
}
